import React, { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  CreditCard, Upload, Camera, AlertTriangle, CheckCircle,
  Calendar, DollarSign, User, Phone, Building, FileText,
  Clock, RefreshCw, Eye, X, ArrowRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../utils/axios.config';

const EnhancedPaymentSubmit = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    reset
  } = useForm();

  const paymentMethod = watch('paymentMethod');
  const amount = watch('amount');

  // Fetch payment summary for context
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['paymentSummary'],
    queryFn: async () => await api.get('finance/payment/summary'),
    staleTime: 5 * 60 * 1000,
  });

  const paymentSummary = summaryData?.data?.data || {};

  // Submit payment mutation
  const submitPaymentMutation = useMutation({
    mutationFn: async (formData) => {
      return api.post('finance/payment/submit', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    onSuccess: () => {
      toast.success('পেমেন্ট সফলভাবে জমা দেওয়া হয়েছে');
      queryClient.invalidateQueries(['paymentSummary']);
      queryClient.invalidateQueries(['myPayments']);
      reset();
      setSelectedFile(null);
      setFilePreview(null);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'পেমেন্ট জমা দিতে ব্যর্থ');
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('শুধুমাত্র JPG, JPEG, PNG ফাইল সাপোর্টেড');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('ফাইলের সাইজ ৫ MB এর কম হতে হবে');
        return;
      }

      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (data) => {
    const formData = new FormData();
    
    // Add form fields
    Object.keys(data).forEach(key => {
      if (data[key] && key !== 'screenshot') {
        formData.append(key, data[key]);
      }
    });

    // Add file if selected
    if (selectedFile) {
      formData.append('screenshot', selectedFile);
    }

    submitPaymentMutation.mutate(formData);
  };

  const paymentMethods = [
    { value: 'bkash', label: 'bKash', logo: '/logos/bkash.png', bgColor: 'bg-pink-50 border-pink-200' },
    { value: 'nagad', label: 'Nagad', logo: '/logos/nagad.png', bgColor: 'bg-orange-50 border-orange-200' },
    { value: 'rocket', label: 'Rocket', logo: '/logos/rocket.png', bgColor: 'bg-purple-50 border-purple-200' },
    { value: 'upay', label: 'Upay', logo: '/logos/upay.png', bgColor: 'bg-red-50 border-red-200' },
    { value: 'bank', label: 'Bank Transfer', icon: Building, bgColor: 'bg-blue-50 border-blue-200' },
    { value: 'cash', label: 'Cash', icon: DollarSign, bgColor: 'bg-green-50 border-green-200' },
  ];

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">পেমেন্ট জমা দিন</h1>
        <p className="text-gray-600">মাসিক সদস্যপদ ফি ও অন্যান্য পেমেন্ট জমা দিন</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Payment Details Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCard className="w-5 h-5 mr-2 text-primary" />
                পেমেন্টের বিবরণ
              </h2>
              
              <div className="space-y-6">
                {/* Month Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    মাস নির্বাচন করুন *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      {...register('month', {
                        required: 'মাস নির্বাচন আবশ্যক',
                        pattern: {
                          value: /^(0[1-9]|1[0-2])-\d{4}$/,
                          message: 'সঠিক ফরম্যাট: MM-YYYY'
                        }
                      })}
                      type="text"
                      placeholder="MM-YYYY (যেমন: 01-2024)"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  {errors.month && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertTriangle size={16} className="mr-1" />
                      {errors.month.message}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    ফরম্যাট: MM-YYYY (যেমন: জানুয়ারি ২০২৪ এর জন্য 01-2024)
                  </p>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    পরিমাণ (টাকা) *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      {...register('amount', {
                        required: 'পেমেন্টের পরিমাণ আবশ্যক',
                        min: { value: 1, message: 'ন্যূনতম ১ টাকা' },
                        max: { value: 10000, message: 'সর্বোচ্চ ১০,০০০ টাকা' }
                      })}
                      type="number"
                      min="1"
                      placeholder="পরিমাণ লিখুন"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  {errors.amount && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertTriangle size={16} className="mr-1" />
                      {errors.amount.message}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    মাসিক ফি: {paymentSummary.monthlyFee || 50} টাকা
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Method Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                পেমেন্ট পদ্ধতি নির্বাচন করুন *
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {paymentMethods.map((method) => {
                  const IconComponent = method.icon;
                  return (
                    <label
                      key={method.value}
                      className={`cursor-pointer border-2 rounded-lg p-4 transition-all duration-200 hover:shadow-md ${
                        paymentMethod === method.value
                          ? 'border-primary bg-primary/5'
                          : `border-gray-200 ${method.bgColor} hover:border-gray-300`
                      }`}
                    >
                      <input
                        {...register('paymentMethod', { required: 'পেমেন্ট পদ্ধতি নির্বাচন আবশ্যক' })}
                        type="radio"
                        value={method.value}
                        className="sr-only"
                      />
                      <div className="flex flex-col items-center text-center">
                        {method.logo ? (
                          <img
                            src={method.logo}
                            alt={method.label}
                            className="w-8 h-8 mb-2"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                        ) : null}
                        {IconComponent && (
                          <IconComponent 
                            className={`w-8 h-8 mb-2 ${method.logo ? 'hidden' : 'block'}`} 
                          />
                        )}
                        <span className="text-sm font-medium text-gray-900">
                          {method.label}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
              
              {errors.paymentMethod && (
                <p className="mt-3 text-sm text-red-600 flex items-center">
                  <AlertTriangle size={16} className="mr-1" />
                  {errors.paymentMethod.message}
                </p>
              )}
            </div>

            {/* Transaction Details Card */}
            {paymentMethod && paymentMethod !== 'cash' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  লেনদেনের বিবরণ
                </h2>
                
                <div className="space-y-6">
                  {/* Transaction ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      লেনদেন আইডি / Transaction ID *
                    </label>
                    <input
                      {...register('transactionId', {
                        required: paymentMethod !== 'cash' ? 'লেনদেন আইডি আবশ্যক' : false,
                        minLength: { value: 5, message: 'ন্যূনতম ৫ অক্ষর' }
                      })}
                      type="text"
                      placeholder="TXN123456789"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono"
                    />
                    {errors.transactionId && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertTriangle size={16} className="mr-1" />
                        {errors.transactionId.message}
                      </p>
                    )}
                  </div>

                  {/* Account Number (for bank) */}
                  {paymentMethod === 'bank' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        অ্যাকাউন্ট নম্বর *
                      </label>
                      <input
                        {...register('accountNumber', {
                          required: paymentMethod === 'bank' ? 'অ্যাকাউন্ট নম্বর আবশ্যক' : false
                        })}
                        type="text"
                        placeholder="১২৩৪৫৬৭৮৯০"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      {errors.accountNumber && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertTriangle size={16} className="mr-1" />
                          {errors.accountNumber.message}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Screenshot Upload Card */}
            {paymentMethod && paymentMethod !== 'cash' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Camera className="w-5 h-5 mr-2 text-primary" />
                  পেমেন্ট স্ক্রিনশট আপলোড করুন *
                </h2>
                
                <div className="space-y-4">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                  />
                  
                  {!filePreview ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors duration-200"
                    >
                      <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-lg font-medium text-gray-900 mb-2">
                        স্ক্রিনশট আপলোড করুন
                      </p>
                      <p className="text-sm text-gray-600 mb-4">
                        ক্লিক করুন বা ছবি ড্রাগ করে এনে ছাড়ুন
                      </p>
                      <p className="text-xs text-gray-500">
                        JPG, JPEG, PNG (সর্বোচ্চ ৫MB)
                      </p>
                    </div>
                  ) : (
                    <div className="relative">
                      <img
                        src={filePreview}
                        alt="Payment screenshot"
                        className="w-full h-64 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute top-2 right-2 flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setShowPreview(true)}
                          className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors duration-200"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={removeImage}
                          className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors duration-200"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {!selectedFile && paymentMethod !== 'cash' && (
                    <p className="text-sm text-red-600 flex items-center">
                      <AlertTriangle size={16} className="mr-1" />
                      পেমেন্ট স্ক্রিনশট আবশ্যক
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitPaymentMutation.isLoading}
                className="px-8 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
              >
                {submitPaymentMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                    জমা দেওয়া হচ্ছে...
                  </>
                ) : (
                  <>
                    পেমেন্ট জমা দিন
                    <ArrowRight size={20} className="ml-2" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Current Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              বর্তমান স্থিতি
            </h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-600">মাসিক ফি</span>
                <span className="text-lg font-bold text-primary">
                  ৳{paymentSummary.monthlyFee || 50}
                </span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-600">এই মাস</span>
                <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
                  paymentSummary.currentMonth?.status === 'approved'
                    ? 'bg-green-100 text-green-800'
                    : paymentSummary.currentMonth?.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {paymentSummary.currentMonth?.status === 'approved' ? 'পরিশোধিত' :
                   paymentSummary.currentMonth?.status === 'pending' ? 'অপেক্ষমাণ' : 'অপরিশোধিত'}
                </span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-600">পরিশোধিত মাস</span>
                <span className="text-lg font-bold text-green-600">
                  {paymentSummary.totalPaid || 0}
                </span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-600">বকেয়া মাস</span>
                <span className="text-lg font-bold text-red-600">
                  {paymentSummary.totalDue || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              গুরুত্বপূর্ণ নির্দেশনা
            </h3>
            
            <ul className="text-sm text-blue-800 space-y-2">
              <li className="flex items-start">
                <CheckCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                সঠিক মাস ও পরিমাণ নিশ্চিত করুন
              </li>
              <li className="flex items-start">
                <CheckCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                পেমেন্টের স্পষ্ট স্ক্রিনশট দিন
              </li>
              <li className="flex items-start">
                <CheckCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                লেনদেন আইডি সঠিকভাবে লিখুন
              </li>
              <li className="flex items-start">
                <CheckCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                অনুমোদনের জন্য ২৪-৪৮ ঘণ্টা অপেক্ষা করুন
              </li>
            </ul>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              দ্রুত অ্যাকশন
            </h3>
            
            <div className="space-y-3">
              <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200 flex items-center">
                <FileText size={16} className="mr-3" />
                আমার পেমেন্ট ইতিহাস
              </button>
              <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200 flex items-center">
                <RefreshCw size={16} className="mr-3" />
                স্থিতি রিফ্রেশ করুন
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {showPreview && filePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                পেমেন্ট স্ক্রিনশট
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-4">
              <img
                src={filePreview}
                alt="Payment screenshot preview"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedPaymentSubmit;